import { validateShippingForm } from '@page-components/order/common';
import type { FulfillmentProviderOption } from '@page-components/order/ShippingLabelComponents/useProviderOptions';
import { ErrorMessageConstant } from '@utility/constants/common';
import ShippingLabelService from '@utility/services/shipping-label';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@utility/services/base';
import { orderKeys } from '@features/orders/lib/query-keys';
import { firebase, useFirebaseSession } from '@utility/services/firebase';
import { ToastMessage } from '@utility/toast';
import { useAuth } from '@contexts/auth';
import { IntegrationType } from '@features/storage-providers/lib/integration';
import { CommonUtility } from '@utility/common';
import OrdersService from '@utility/services/order';
import { useAsyncDebounce, useDebouncedEffect } from './debounceAsync';
import { isMongoOrderId } from './address-validation-id';

/* Label queries sit under the order's, so an order's caches go together. */
const labelKeys = {
  metadata: () => [...orderKeys.all, 'label-metadata'] as const,
  labels: (orderId?: string) =>
    [...orderKeys.all, 'labels', orderId ?? null] as const,
  fulfillMetadata: (orderId: string) =>
    [...orderKeys.all, 'fulfill-metadata', orderId] as const,
  aliasInventory: (providerId: string, itemIds: unknown) =>
    [...orderKeys.all, 'alias-inventory', providerId, itemIds] as const,
  returnsRate: (orderId: string, providerId: string, lines: unknown) =>
    [...orderKeys.all, 'returns-rate', orderId, providerId, lines] as const,
  rate: (orderId: string, form: unknown) =>
    [...orderKeys.all, 'label-rate', orderId, form] as const,
  warehouses: (orderId: string, batchId: unknown) =>
    [...orderKeys.all, 'label-warehouses', orderId, batchId ?? null] as const,
  warehouseRates: (orderId: string, mode: string, inputs: unknown) =>
    [...orderKeys.all, 'label-warehouse-rates', orderId, mode, inputs] as const,
};

const noWarehouses: any[] = [];
import type { OrderShippingLabel } from '@skupreme/api-contracts/core';
import type { ShippingOrderRequest } from '@features/order-details/lib/fulfillment-request';

type ProviderInventoryItem = {
  available?: number;
};

function getProviderInventory(provider: {
  items?: ProviderInventoryItem[];
}): ProviderInventoryItem[] {
  return provider.items || [];
}

/*
 * The same warehouse can come back twice, once virtual and once not, and the
 * two report different parts of the order. Quoting the one that reports more of
 * it keeps the request whole; quoting the other would price a few lines or none.
 */
function pickWarehouse<T extends { _id?: string; items?: unknown[] }>(
  warehouses: T[] | null,
  providerId: string | null,
) {
  const matches = (warehouses || []).filter(
    warehouse => String(warehouse._id) === String(providerId),
  );

  if (matches.length < 2) {
    return matches;
  }

  return [
    matches.reduce((best, warehouse) =>
      (warehouse.items?.length || 0) > (best.items?.length || 0)
        ? warehouse
        : best,
    ),
  ];
}

export const shippingLabelMetadataHook = (
  isInternational = false,
  isShipManual = false,
) => {
  const query = useQuery({
    queryKey: labelKeys.metadata(),
    queryFn: async () =>
      ((await ShippingLabelService.metadata()).data ?? []) as any[],
    staleTime: Number.POSITIVE_INFINITY,
  });

  /* Each carrier with only the services for this destination; a manual ship lists every carrier. */
  const data = useMemo(() => {
    if (!query.data) return false;
    const carriers = query.data.map(label => ({
      ...label,
      service: label.service.filter(x => x?.international === isInternational),
    }));
    return isShipManual
      ? carriers
      : carriers.filter(label => label.service.length && !label.manual);
  }, [query.data, isInternational, isShipManual]);

  return {
    data,
    loading: query.isFetching,
    error: query.error
      ? query.error.message || ErrorMessageConstant.defaultAPIError
      : '',
    fetchData: () => query.refetch(),
  };
};

const noLabels: OrderShippingLabel[] = [];

/* The labels belong to the order that was asked for: another order starts empty, not with these. */
export const shippingLabelsHook = (orderId?: string) => {
  const queryClient = useQueryClient();
  const key = labelKeys.labels(orderId);
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const result = await ShippingLabelService.getLabels(orderId as string);
      return {
        data: (result.data ?? []) as OrderShippingLabel[],
        returns: (result.returns_data ?? []) as OrderShippingLabel[],
      };
    },
    enabled: !!orderId,
  });

  /* A refund marks the labels in place. */
  const update =
    (field: 'data' | 'returns') => (labels: OrderShippingLabel[]) =>
      queryClient.setQueryData(key, previous => ({
        data: noLabels,
        returns: noLabels,
        ...(previous as object),
        [field]: labels,
      }));

  return {
    data: query.data?.data ?? noLabels,
    returnData: query.data?.returns ?? noLabels,
    setReturnData: update('returns'),
    loading: !!orderId && (query.isPending || query.isFetching),
    error: query.error
      ? query.error.message || ErrorMessageConstant.defaultAPIError
      : '',
    fetchData: async () => {
      await query.refetch();
    },
    setData: update('data'),
  };
};

/**
 * `all` quotes every warehouse up front, which is what the overlay's dropdown
 * needs to sort them by price. `selected` quotes only the chosen one, so the
 * details page pays for a single request instead of one per warehouse.
 */
export type RateScope =
  | { mode: 'all' }
  | { mode: 'selected'; providerId?: string | null };

/** Lines the B2B warehouse alone stocks: real stock, just not shippable on a D2C label. */
export type B2bWarehouse = {
  reason: string;
  item_ids: string[];
  provider_names: string[];
};

const keepsVirtualTypes = [
  IntegrationType['3pl'],
  IntegrationType.tactical3pl,
  IntegrationType.manual,
];

const hasAvailable = provider =>
  provider.is_oos_fulfillable
    ? provider.items?.some(x => x.available && x.available !== 999999)
    : provider.items?.some(x => x.available);

/*
 * One entry per warehouse: a virtual one replaces the real one with the same
 * id unless the real one covers more items. JM Group also flags the warehouses
 * without stock when FBA or another warehouse has some.
 */
const mergeWarehouses = (providers: any[], isJMGroup: boolean) => {
  const real = providers.filter(
    x => !x.virtual || keepsVirtualTypes.includes(x.integration_type),
  );
  const unique = real.filter(
    (x, index) => real.findIndex(y => y._id === x._id) === index,
  );
  const virtual = providers.filter(x => x.virtual);
  let merged = unique.map(provider => {
    const twin = virtual.find(x => x._id === provider._id);
    return !twin || twin.itemsLength < provider.itemsLength ? provider : twin;
  });
  virtual.forEach(provider => {
    if (!merged.find(x => x._id === provider._id)) merged.push(provider);
  });

  if (isJMGroup) {
    const fba = merged.find(x => x.integration_type === IntegrationType.spapi);
    if (fba) {
      const fbaHasInventory = fba.items.some(x => x.available);
      const otherHasInventory = merged.find(
        x => x.integration_type !== IntegrationType.spapi && hasAvailable(x),
      );
      if (fbaHasInventory || otherHasInventory) {
        merged = merged.map(provider => ({
          ...provider,
          showOutOfStockBadge: !hasAvailable(provider),
        }));
      }
    }
  }
  return merged;
};

/** One warehouse in the Ship from list. */
export type WarehouseOption = FulfillmentProviderOption & {
  item: NonNullable<FulfillmentProviderOption['item']>;
};

const warehouseOption = (item): WarehouseOption => ({
  text: item.name,
  value: item._id,
  item: {
    loading: item.loading,
    rate: item.rate,
    packages: item.packages,
    is_international: item.is_international,
    isFBA: item.fba,
    country: item.country,
    address: item.address,
    showOutOfStockBadge: item.showOutOfStockBadge,
    /* Each warehouse ships at its own speeds, not at a fixed set of three. */
    shipping_speed: item.shipping_speed,
    integration_type: item.integration_type,
    items: getProviderInventory(item),
  },
});

const labelErrorMessage = (error: any) => {
  if (CommonUtility.isString(error?.error)) return error.error;
  if (CommonUtility.isString(error?.message)) return error.message;
  return ErrorMessageConstant.defaultAPIError;
};

type RateLine = { item_id: string; qty: number; checked?: boolean };
type WarehouseRate = {
  sentItems?: { item_id: string; qty: number }[];
  rate?: number | null;
  packages?: unknown;
  fetchRateError?: string;
  loading?: boolean;
};

/*
 * Each warehouse's rate on its own, so one that fails still lets the others
 * show. FBA is asked once and its answer goes to every FBA warehouse.
 */
const fetchWarehouseRates = async (
  orderId,
  warehouses: any[],
  lines: RateLine[],
  formValues,
  quotesEveryProvider: boolean,
) => {
  const rates: Record<string, WarehouseRate> = {};
  let fbaFetched = false;
  let fbaRate = 0;
  let fbaPackages = [];
  const checked = lines.filter(x => !!x.checked);

  for (const warehouse of warehouses) {
    if (warehouse.integration_type === 'manual') continue;
    const stocked = checked.filter(x =>
      (warehouse.items || []).some(item => item._id === x.item_id),
    );
    /*
     * A warehouse that stocks none of the checked lines still has to be asked,
     * so the rate call can name the SKU and the warehouse. Sending nothing
     * comes back as an empty quote and the generic package-details line.
     */
    const itemsToSend = (stocked.length ? stocked : checked).map(item => ({
      item_id: item.item_id,
      qty: item.qty,
    }));
    const rate: WarehouseRate = { sentItems: itemsToSend };
    try {
      if (warehouse.fba) {
        if (!fbaFetched) {
          const info = await OrdersService.providerFBAShippingRate({
            items: itemsToSend,
            order_id: orderId,
            isFBA: true,
          });
          fbaFetched = true;
          if (info?.data?.rate) {
            fbaPackages = info.data.packages;
            fbaRate = info.data.rate;
          }
        }
        if (fbaRate) {
          rate.rate = fbaRate;
          rate.packages = fbaPackages;
        }
      } else {
        const info = await OrdersService.providerShippingRate({
          order_id: orderId,
          fulfillment_provider_id: warehouse._id,
          /* The form starts on no speed at all, and an empty one is not a
             speed the warehouse can quote: it ships at its own first. */
          order_speed:
            formValues?.order_speed || warehouse?.shipping_speed?.[0],
          items: itemsToSend,
          options: { signature: formValues?.signature || false },
          /* A warehouse the operator picked: a failed quote is worth an
             order_error. The every-warehouse sweep is not. */
          ...(!quotesEveryProvider && { record_failure: true }),
        });
        if (info?.totalRate) {
          rate.rate = info.totalRate;
          rate.packages = info.packages;
        }
      }
    } catch (error) {
      rate.rate = null;
      rate.packages = [];
      rate.fetchRateError = labelErrorMessage(error);
    }
    rates[warehouse._id] = rate;
  }
  return rates;
};

const loadingRates = (warehouses: any[]) =>
  Object.fromEntries(
    warehouses
      .filter(warehouse => warehouse.integration_type !== 'manual')
      .map(warehouse => [warehouse._id, { loading: true }]),
  );

export const shippingLabelFPHook = (
  orderId,
  batchId,
  items,
  formValues,
  scope: RateScope = { mode: 'all' },
) => {
  const quotesEveryProvider = scope.mode === 'all';
  const rateProviderId =
    scope.mode === 'selected' ? scope.providerId || null : null;

  const providersQuery = useQuery({
    queryKey: labelKeys.warehouses(orderId, batchId),
    queryFn: () =>
      ShippingLabelService.fulfillmentProviders(orderId, {
        batchId: batchId || undefined,
      }),
    enabled: !!orderId,
  });
  const allData: any[] = providersQuery.data?.data ?? noWarehouses;

  const data = useMemo(
    () =>
      mergeWarehouses(
        allData.map(provider => ({
          ...provider,
          loading: provider.integration_type !== 'manual',
        })),
        !!formValues?.isJMGroup,
      ),
    [allData, formValues?.isJMGroup],
  );
  const list = useMemo(() => data.map(warehouseOption), [data]);

  /* Quotes follow the checked lines, the form and the picked warehouse once they settle. */
  const lines: RateLine[] = (items || []).map(item => ({
    item_id: item._id,
    qty: item?.qty,
    checked: item?.checked,
  }));
  const settled = useAsyncDebounce(
    JSON.stringify({ lines, formValues, rateProviderId }),
    500,
  ) as string | undefined;
  const inputs = useMemo(
    () => (settled ? JSON.parse(settled) : null),
    [settled],
  );
  const warehouses = quotesEveryProvider
    ? allData
    : pickWarehouse(allData, inputs?.rateProviderId ?? null);

  const ratesQuery = useQuery({
    queryKey: labelKeys.warehouseRates(orderId, scope.mode, inputs),
    queryFn: () =>
      fetchWarehouseRates(
        orderId,
        warehouses,
        inputs.lines,
        inputs.formValues,
        quotesEveryProvider,
      ),
    enabled:
      !!inputs &&
      (quotesEveryProvider || !!inputs.rateProviderId) &&
      CommonUtility.isValidArray(inputs.lines) &&
      CommonUtility.isValidArray(allData),
  });

  /* Quoting one warehouse must not drop what the others already quoted. */
  const quoted = useRef<{
    orderId: unknown;
    rates: Record<string, WarehouseRate>;
  }>({ orderId, rates: {} });
  if (quoted.current.orderId !== orderId) {
    quoted.current = { orderId, rates: {} };
  }
  if (ratesQuery.data) {
    quoted.current.rates = quotesEveryProvider
      ? ratesQuery.data
      : { ...quoted.current.rates, ...ratesQuery.data };
  }
  /* The warehouses being asked show a spinner; every warehouse does before the first sweep. */
  const asking =
    ratesQuery.isFetching || (quotesEveryProvider && !ratesQuery.data)
      ? loadingRates(warehouses)
      : {};
  const providerRates = { ...quoted.current.rates, ...asking };

  return {
    data,
    loading:
      (!!orderId && providersQuery.isPending) ||
      providersQuery.isFetching ||
      ratesQuery.isFetching,
    error: providersQuery.error ? labelErrorMessage(providersQuery.error) : '',
    list,
    fetchData: () => providersQuery.refetch(),
    providerRates,
    b2bWarehouse: (providersQuery.data?.b2b_warehouse ??
      null) as B2bWarehouse | null,
  };
};

export const shippingReturnsRateHook = (
  order_id,
  fulfillment_provider_id,
  items,
) => {
  const lines = (items ?? []).map(item => ({
    item_id: item._id,
    qty: item.newQty || item?.content?.qty,
  }));
  const query = useQuery({
    queryKey: labelKeys.returnsRate(order_id, fulfillment_provider_id, lines),
    queryFn: async ({ signal }) => {
      try {
        return await OrdersService.providerReturnsRate(
          {
            order_id,
            fulfillment_provider_id,
            order_speed: 'Standard',
            items: lines,
          },
          signal,
        );
      } catch (error) {
        if (error?.error) ToastMessage.error(error.error);
        throw error;
      }
    },
    enabled: !!order_id && !!fulfillment_provider_id && !!lines.length,
  });

  return {
    data: query.data ?? null,
    loading: query.isFetching,
    fetchData: () => query.refetch(),
  };
};

type RateHookReturnType = {
  rate: number | null;
  time: string | null;
  loading: boolean;
  error: string;
  rateNegotiate: boolean;
  transactionId: string | null;
  ddp: DDP | null;
};

type Rate = {
  value: number;
  unit: string;
};

type Time = {
  value: number;
  unit: string;
};

type DDP = {
  duties: number;
  taxes: number;
  fees: number;
  total: number;
  landed_cost_id: string;
  currency: string;
  guarantee_code: string;
};

type TransactionData = {
  transaction_id: string;
  rate: Rate;
  time: Time;
  service: string;
  package: string;
  ddp?: DDP;
  shipping_rate: number;
  negotiated_rate: boolean;
};

type rateResponseType = {
  data: TransactionData;
};

/* What the rate call takes: weights as numbers, no form-only fields, no dimensions for a letter. */
const rateRequest = (data, orderId) => {
  const { saveDefault, insurance, signature, ...rest } = data;
  const request = {
    ...rest,
    pounds: Number.parseFloat(data?.pounds || 0),
    ounces: Number.parseFloat(data?.ounces || 0),
    order_id: orderId,
    girth: 0,
    mode: 'production',
    ...(signature ? { options: { signature: true } } : {}),
  };
  if (request.package === 'letter') {
    delete request.length;
    delete request.width;
    delete request.height;
  }
  return request;
};

const rateError = error =>
  error?.error && typeof error.error === 'string'
    ? error.error
    : ErrorMessageConstant.defaultAPIError;

export const rateHook = (
  formData: {
    pounds: string;
    ounces: string;
    package: string;
    service: string;
    length?: string;
    width?: string;
    height?: string;
    provider: string;
    shipping_speed?: string;
    signature?: boolean;
  },
  orderId: string,
  show: {
    dimensions: boolean;
    weight: boolean;
  },
): RateHookReturnType => {
  /* The form hands a new object on every render, so the text is what settles. */
  const settled = useAsyncDebounce(JSON.stringify(formData ?? null), 500) as
    | string
    | undefined;
  const value = useMemo(
    () => (settled ? JSON.parse(settled) : null),
    [settled],
  );
  const ready =
    !!value &&
    Object.values(value).some(x => !!x) &&
    validateShippingForm(value, show);

  const query = useQuery<rateResponseType>({
    queryKey: labelKeys.rate(orderId, value),
    queryFn: ({ signal }) =>
      ShippingLabelService.rate(rateRequest(value, orderId), signal),
    enabled: !!orderId && ready,
  });

  const quote = query.data?.data;
  /* No weight, no rate: the last quote does not stand for an empty parcel. */
  const weighed =
    !!Number.parseFloat(formData?.pounds) ||
    !!Number.parseFloat(formData?.ounces);
  const failed = query.error as { status?: number } | null;

  return {
    rate: quote && weighed ? quote.rate.value : null,
    time: quote
      ? quote.time?.value
        ? `${quote.time.value} ${quote.time.unit}`
        : '-'
      : null,
    loading: query.isFetching,
    error: failed?.status ? rateError(failed) : '',
    rateNegotiate: !!quote?.negotiated_rate,
    transactionId: quote?.transaction_id ?? null,
    ddp: quote && CommonUtility.isValidObject(quote.ddp) ? quote.ddp! : null,
  };
};

const ADDRESS_VALIDATION_STALE_TIME = 30_000;

type AddressValidationResponse = {
  data: { is_valid?: boolean; error?: string } | null;
};

/*
 * The details page and the fulfillment form derive the same payload from the
 * same order, so they share one answer instead of validating twice.
 */
export const addressValidationHook = (reqData, orderId, doValidate = true) => {
  const payload = useMemo(() => {
    if (!reqData || !isMongoOrderId(orderId)) {
      return null;
    }
    const temp = { order_id: orderId, ...reqData };
    delete temp.phone;
    delete temp.name;
    delete temp.email;
    delete temp.company;
    return temp;
  }, [reqData, orderId]);

  const query = useQuery<AddressValidationResponse, ApiError>({
    queryKey: orderKeys.addressValidation(payload),
    queryFn: () => ShippingLabelService.addressValidation(payload),
    enabled: !!payload && doValidate,
    staleTime: ADDRESS_VALIDATION_STALE_TIME,
  });

  const result = query.data?.data ?? undefined;

  const error = (() => {
    if (query.error) {
      return query.error.error || ErrorMessageConstant.defaultAPIError;
    }
    return result && !result.is_valid ? result.error || '' : '';
  })();

  return {
    data: result,
    loading: query.isFetching,
    error,
    valid: query.error ? false : !result || !!result.is_valid,
  };
};

/* `enabled` false (an orders_page_v2 account, EN-4117): no Firebase session and no listener. */
export const shippingOrderQueueHook = (enabled = true) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentOrg: organizationId } = useAuth();
  const firebaseReady = useFirebaseSession(enabled && !!organizationId);

  useEffect(() => {
    if (!enabled || !firebaseReady || !organizationId) return;

    return firebase
      .firestore()
      .collection(`shipping_labels/${organizationId}/requests`)
      .where('status', '!=', 'completed')
      .onSnapshot(async snap => {
        const temp = snap.docs.map(docSnapshot => docSnapshot.data());
        // @ts-expect-error - TS2345: Argument of type 'DocumentData[]' is not assignable to parameter of type 'SetSta...
        setData(temp);
        setLoading(false);
      });
  }, [enabled, firebaseReady, organizationId]);

  return { data, loading };
};

export const shippingOrderFirebaseHook = (id?: string, enabled = true) => {
  const [data, setData] = useState<ShippingOrderRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentOrg: organizationId } = useAuth();
  const firebaseReady = useFirebaseSession(enabled && !!organizationId);

  /*
   * One order at a time: the requests of the order left behind say nothing
   * about the one now on screen, and a listener left running would go on
   * answering for it. Both matter when fulfilling a queue, which moves from
   * order to order without ever leaving the page.
   */
  useEffect(() => {
    setData([]);

    if (!enabled || !id || !organizationId || !firebaseReady) {
      return;
    }

    return firebase
      .firestore()
      .collection(`shipping_labels/${organizationId}/requests`)
      .where('order_id', '==', id)
      .onSnapshot(snap => {
        setData(
          snap.docs.map(docSnapshot => {
            const request = docSnapshot.data();
            return {
              status:
                typeof request.status === 'string' ? request.status : undefined,
              shipping_state:
                typeof request.shipping_state === 'string'
                  ? request.shipping_state
                  : undefined,
            };
          }),
        );
        setLoading(false);
      });
  }, [enabled, id, organizationId, firebaseReady]);

  return { data, loading };
};

/**
 * Hook For Handling Manual Shipping Orders Related To Shipping Labels
 * @param {string} orderId - Id Of The Order
 * @returns {Object} - Returns An Object Containing Functions And States For Handling Manual Shipping Orders
 */
export const useShippingLabelsManualShipOrder = orderId => {
  const mutation = useMutation({
    mutationFn: data =>
      ShippingLabelService.fetchShippingLabelsManualShipOrder(orderId, data),
  });

  /* Resolves true once the ship request succeeded. The answer is reported as a
     return value, not through state: the API settles the fulfillment request
     before it answers, and the order page swaps the shipping form out as soon
     as that request shows up, so a state update here would land on an
     unmounted component and any effect waiting on it would never run. */
  const { mutateAsync } = mutation;
  const handleSubmit = useCallback(
    async (data): Promise<boolean> => {
      try {
        await mutateAsync(data);
        ToastMessage.success('Tracking information added successfully');
        return true;
      } catch (error) {
        ToastMessage.apiError(error);
        return false;
      }
    },
    [mutateAsync],
  );

  return {
    handleSubmit,
    loading: mutation.isPending,
  };
};

export const useHighestQuantityAliasInventory = (
  fulfillment_provider_id,
  orderItems,
) => {
  const itemIds = useMemo(() => orderItems?.map(x => x._id), [orderItems]);
  const enabled =
    !!fulfillment_provider_id && CommonUtility.isValidArray(itemIds);
  const query = useQuery({
    queryKey: labelKeys.aliasInventory(fulfillment_provider_id, itemIds),
    queryFn: async () =>
      ((
        await ShippingLabelService.getProviderHighestAlias({
          fulfillment_provider_id,
          items_id: itemIds,
        })
      )?.data ?? []) as any[],
    enabled,
  });

  return {
    data: query.data ?? (enabled ? [] : null),
    loading: query.isFetching,
    error: (query.error as { error?: string } | null)?.error ?? '',
  };
};

export const shippingOrderFulfillMetadataHook = orderId => {
  const query = useQuery({
    queryKey: labelKeys.fulfillMetadata(orderId),
    queryFn: async () =>
      ((await ShippingLabelService.orderFulfillMetadata(orderId))?.data ??
        []) as any[],
    enabled: !!orderId,
  });
  return {
    data: query.data ?? [],
    loading: query.isFetching,
    error: query.error ?? false,
  };
};
